<template>
    <!-- Polylines par branche -->
    <template v-for="pl in polylines" :key="pl.branchId">
        <l-polyline :lat-lngs="pl.latLngs" :color="pl.color" :opacity="0.65" :weight="6" />
    </template>

    <!-- Start / End (par défaut sur la branche principale) -->
    <NotificationMaker v-if="startMarker && startMarker.latitude != null" notification="start"
        :latitude="startMarker.latitude" :longitude="startMarker.longitude" />
    <NotificationMaker v-if="endMarker && endMarker.latitude != null" notification="finish"
        :latitude="endMarker.latitude" :longitude="endMarker.longitude" />
</template>

<script>
import { LPolyline } from "@vue-leaflet/vue-leaflet";
import NotificationMaker from "./NotificationMarker.vue";
import CorridoringService from "@/services/corridoring.service.js";
import { normalizePositions } from "../composables/usePositions";

export default {
    name: "CorridoringDisplay",
    components: { LPolyline, NotificationMaker },
    props: {
        corridoringId: { type: [Number, String], required: true },
    },
    data() {
        return {
            positions: [],
        };
    },
    computed: {
        // Regroupe les positions par branchId et trie par seq
        polylines() {
            const src = this.positions;
            if (!Array.isArray(src) || src.length === 0) return [];

            const colorOf = (branchId) => {
                if (branchId === 1) return "#0d6efd"; // bleu actuel
                if (branchId === 2) return "#198754"; // vert
                if (branchId === 3) return "#fd7e14"; // orange
                if (branchId === 4) return "#212529"; // noir
                return "#6c757d"; // gris fallback
            };

            const byBranch = new Map();

            for (const p of src) {
                const branchId = Number(p.branchId ?? 1);
                const seq = Number(p.seq ?? 0);
                const lat = Number(p.latitude ?? p.lat);
                const lng = Number(p.longitude ?? p.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

                if (!byBranch.has(branchId)) byBranch.set(branchId, []);
                byBranch.get(branchId).push({ seq, lat, lng });
            }

            return Array.from(byBranch.entries())
                .sort(([a], [b]) => a - b)
                .map(([branchId, pts]) => {
                    pts.sort((a, b) => a.seq - b.seq);
                    return {
                        branchId,
                        color: colorOf(branchId),
                        latLngs: pts.map((x) => [x.lat, x.lng]),
                    };
                })
                .filter((pl) => pl.latLngs.length >= 2);
        },

        // Start / end calculés sur la branche startEndBranchId
        startMarker() {
            const branch1 = this.getBranchPositions(1);
            return branch1.length ? branch1[0] : null;
        },
        endMarker() {
            const branch1 = this.getBranchPositions(1);
            return branch1.length ? branch1[branch1.length - 1] : null;
        },
    },
    watch: {
        corridoringId: {
            immediate: true,
            handler() {
                this.loadIfNeeded();
            },
        },
    },
    methods: {
        getBranchPositions(branchId) {
            const src = this.positions;
            if (!Array.isArray(src) || src.length === 0) return [];

            const bid = Number(branchId);
            return src
                .filter((p) => Number(p.branchId ?? 1) === bid)
                .slice()
                .sort((a, b) => Number(a.seq ?? 0) - Number(b.seq ?? 0));
        },

        async loadIfNeeded() {

            const id = Number(this.corridoringId);
            if (!Number.isFinite(id)) {
                this.positions = [];
                return;
            }

            try {
                const resp = await CorridoringService.getCorridoringPositions(id);
                this.positions = normalizePositions(resp.data);
                this.$emit("loaded", this.positions);
            } catch (e) {
                console.error("CorridoringDisplay: failed to load positions", e);
                this.positions = [];
                this.$emit("loaded", []);
            }
        },
    },
};
</script>